import Phaser from "phaser";
import { COLORS, hexToPhaser } from "./colors";
import { SpriteLoader } from "../sprite-loader";
import { ItemDefinition, WheelPrize } from "../models/common";

export interface SpinningWheelOptions {
  prizes: WheelPrize[];
  x?: number;
  y?: number;
  wheelRadius?: number;
  winningPrizeIndex?: number; // Pre-determined winning prize index
  onSpinComplete?: (winningPrize: WheelPrize) => void;
  autoStart?: boolean; // Auto-start spinning
}

export class SpinningWheel {
  private scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container;
  private wheelRadius: number;
  private prizes: WheelPrize[];
  private segments: Phaser.GameObjects.Graphics[] = [];
  private prizeIcons: Phaser.GameObjects.Sprite[] = [];
  private prizeTexts: Phaser.GameObjects.Text[] = [];
  private wheel: Phaser.GameObjects.Container;
  private wheelShadow: Phaser.GameObjects.Container | null = null;
  private isSpinning = false;
  private spinButton: Phaser.GameObjects.Rectangle | null = null;
  private centerPointer: Phaser.GameObjects.Graphics | null = null;
  private pointerShadow: Phaser.GameObjects.Graphics | null = null;
  private onSpinComplete: ((prize: WheelPrize) => void) | null = null;
  private currentTween: Phaser.Tweens.Tween | null = null;
  private winningPrizeIndex: number = 0;
  private autoStart: boolean = false;

  constructor(scene: Phaser.Scene, options: SpinningWheelOptions) {
    this.scene = scene;
    this.prizes = options.prizes;
    this.wheelRadius = options.wheelRadius || 120;
    this.onSpinComplete = options.onSpinComplete || null;
    this.winningPrizeIndex = options.winningPrizeIndex || 0;
    this.autoStart = options.autoStart ?? true;

    // Create main container
    this.container = scene.add.container(options.x || 0, options.y || 0);
    this.container.setScrollFactor(0);

    if (scene.cameras.cameras.length > 1) {
      scene.cameras.main.ignore(this.container);
    }

    // Create shadow container for wheel first (so it renders behind)
    this.wheelShadow = this.scene.add.container(0, 6);
    this.wheelShadow.setScrollFactor(0);
    this.container.add(this.wheelShadow);

    // Create wheel container (will be rotated)
    this.wheel = scene.add.container(0, 0);
    this.wheel.setScrollFactor(0);
    this.container.add(this.wheel);

    this.drawWheel();

    // Auto-start spinning if enabled
    if (this.autoStart) {
      this.scene.time.delayedCall(100, () => {
        this.startContinuousSpin();
      });
    }
  }

  private drawWheel() {
    const numSegments = this.prizes.length;
    const segmentAngle = (Math.PI * 2) / numSegments;
    const angleOffset = -Math.PI / 2; // Start at top

    for (let i = 0; i < numSegments; i++) {
      const startAngle = i * segmentAngle + angleOffset;
      const endAngle = (i + 1) * segmentAngle + angleOffset;

      // Alternate colors for segments
      const isEvenSegment = i % 2 === 0;
      const segmentColor = isEvenSegment ? 0x00d4ff : 0xff1493;

      // Draw segment shadow
      const shadowSegment = this.scene.add.graphics();
      shadowSegment.fillStyle(0x000000, 0.4);
      shadowSegment.beginPath();
      shadowSegment.moveTo(0, 0);
      shadowSegment.arc(0, 0, this.wheelRadius, startAngle, endAngle, false);
      shadowSegment.lineTo(0, 0);
      shadowSegment.fillPath();
      shadowSegment.setScrollFactor(0);
      this.wheelShadow?.add(shadowSegment);

      // Draw segment background
      const segment = this.scene.add.graphics();
      segment.fillStyle(segmentColor, 1);

      // Draw pie slice
      segment.beginPath();
      segment.moveTo(0, 0);
      segment.arc(0, 0, this.wheelRadius, startAngle, endAngle, false);
      segment.lineTo(0, 0);
      segment.fillPath();

      // Draw segment border
      segment.lineStyle(6, hexToPhaser(COLORS.WHITE), 1);
      segment.beginPath();
      segment.moveTo(0, 0);
      segment.arc(0, 0, this.wheelRadius, startAngle, endAngle, false);
      segment.lineTo(0, 0);
      segment.strokePath();

      segment.setScrollFactor(0);
      this.wheel.add(segment);
      this.segments.push(segment);

      // Add prize icon/text
      const midAngle = startAngle + segmentAngle / 2;
      const iconRadius = this.wheelRadius * 0.65; // Positioned to center within segment

      const iconX = Math.cos(midAngle) * iconRadius;
      const iconY = Math.sin(midAngle) * iconRadius;

      const prize = this.prizes[i];

      if (prize.type === "item" && prize.itemDef) {
        // Create item sprite using SpriteLoader
        const frameX = prize.itemDef.spriteX;
        const frameY = prize.itemDef.spriteY;
        const itemSprite = SpriteLoader.createSprite(
          this.scene,
          prize.itemDef.spriteSheet,
          frameX,
          frameY,
          iconX,
          iconY,
          true, // Fixed UI
        );
        itemSprite.setScale(1);
        // Rotate so the sprite's bottom faces the wheel center, matching the segment's orientation
        itemSprite.setRotation(midAngle + Math.PI / 2);
        this.wheel.add(itemSprite);
        this.prizeIcons.push(itemSprite);
      } else if (prize.type === "xp") {
        // Create XP text
        const xpText = this.scene.add.text(iconX, iconY, `+${prize.xpAmount}`, {
          fontSize: "18px",
          color: COLORS.YELLOW,
          fontFamily: "Anton",
          stroke: COLORS.BLACK,
          strokeThickness: 3,
          align: "center",
        });
        xpText.setOrigin(0.5, 0.5);
        xpText.setScrollFactor(0);
        this.wheel.add(xpText);
        this.prizeTexts.push(xpText);
      } else if (prize.type === "none") {
        // Create X text for no reward
        const noneText = this.scene.add.text(iconX, iconY, "X", {
          fontSize: "24px",
          color: COLORS.WHITE,
          fontFamily: "Anton",
          stroke: COLORS.BLACK,
          strokeThickness: 3,
          align: "center",
        });
        noneText.setOrigin(0.5, 0.5);
        noneText.setScrollFactor(0);
        // Rotate X to stand up in the direction of the wheel slice
        noneText.setRotation(midAngle + Math.PI / 2);
        this.wheel.add(noneText);
        this.prizeTexts.push(noneText);
      }
    }

    // Draw center circle
    const center = this.scene.add.graphics();
    center.fillStyle(0x333333, 1);
    center.beginPath();
    center.arc(0, 0, 20, 0, Math.PI * 2);
    center.fillPath();
    center.lineStyle(3, hexToPhaser(COLORS.WHITE), 1);
    center.strokeCircleShape(new Phaser.Geom.Circle(0, 0, 20));
    center.setScrollFactor(0);
    this.wheel.add(center);

    // Draw center circle shadow
    const centerShadow = this.scene.add.graphics();
    centerShadow.fillStyle(0x000000, 0.4);
    centerShadow.beginPath();
    centerShadow.arc(0, 6, 20, 0, Math.PI * 2);
    centerShadow.fillPath();
    centerShadow.setScrollFactor(0);
    this.wheelShadow?.add(centerShadow);

    // Draw center pointer shadow (at top)
    this.pointerShadow = this.scene.add.graphics();
    this.pointerShadow.fillStyle(0x000000, 0.4);
    this.pointerShadow.beginPath();
    this.pointerShadow.moveTo(-15, -this.wheelRadius - 30 + 4);
    this.pointerShadow.lineTo(0, -this.wheelRadius - 10 + 4);
    this.pointerShadow.lineTo(15, -this.wheelRadius - 30 + 4);
    this.pointerShadow.closePath();
    this.pointerShadow.fillPath();
    this.pointerShadow.setScrollFactor(0);
    this.container.add(this.pointerShadow);

    // Draw center pointer (at top)
    this.centerPointer = this.scene.add.graphics();
    this.centerPointer.fillStyle(hexToPhaser(COLORS.WHITE), 1);
    this.centerPointer.beginPath();
    this.centerPointer.moveTo(-15, -this.wheelRadius - 30);
    this.centerPointer.lineTo(0, -this.wheelRadius - 10);
    this.centerPointer.lineTo(15, -this.wheelRadius - 30);
    this.centerPointer.closePath();
    this.centerPointer.fillPath();
    this.centerPointer.setScrollFactor(0);
    this.container.add(this.centerPointer);
  }

  private startContinuousSpin() {
    // Spin continuously at high speed using a looping tween
    this.isSpinning = true;
    this.currentTween = this.scene.tweens.add({
      targets: this.wheel,
      rotation: { from: 0, to: Math.PI * 2 },
      duration: 800, // Faster rotation (0.8 seconds per full rotation)
      ease: "Linear",
      loop: -1, // Infinite loop
      onUpdate: () => {
        // Normalize rotation to 0-2π to prevent overflow
        this.wheel.rotation = this.wheel.rotation % (Math.PI * 2);
      },
    });
  }

  public stopSpin() {
    if (!this.isSpinning) return;

    // Stop the continuous spin and decelerate to the winning prize
    if (this.currentTween) {
      this.currentTween.stop();
    }

    this.isSpinning = true;

    if (this.spinButton) {
      this.spinButton.disableInteractive();
    }

    // Calculate the angle that centers the winning segment at the pointer (top)
    const segmentAngle = (Math.PI * 2) / this.prizes.length;

    // The center of each segment at angle: (segmentIndex * segmentAngle) + (segmentAngle / 2)
    // But accounting for -π/2 offset, the pointer represents segment 0's center
    // To align winning segment center with pointer, rotate by that segment's center angle
    const winningSegmentCenter =
      this.winningPrizeIndex * segmentAngle + segmentAngle / 2;

    // Normalize current rotation to 0-2π range
    const currentRotation = this.wheel.rotation % (Math.PI * 2);

    // To center the segment at the pointer (which is at -π/2), we rotate to negative of segment angle
    let targetRotation = -winningSegmentCenter;

    // Normalize target to positive angle for comparison
    while (targetRotation < 0) {
      targetRotation += Math.PI * 2;
    }

    // Add full rotations until target is ahead of current rotation
    while (targetRotation <= currentRotation) {
      targetRotation += Math.PI * 2;
    }

    // Add one more full rotation for a nice deceleration effect
    targetRotation += Math.PI * 2;

    // Animate the spin with smooth deceleration
    this.currentTween = this.scene.tweens.add({
      targets: this.wheel,
      rotation: targetRotation,
      duration: 3000,
      ease: "Cubic.easeOut",
      onComplete: () => {
        this.isSpinning = false;
        const winningPrize = this.prizes[this.winningPrizeIndex];

        if (this.onSpinComplete) {
          this.onSpinComplete(winningPrize);
        }
        // Note: Don't try to interact with spinButton here as it may have been destroyed
      },
    });
  }

  public spin(onComplete?: (prize: WheelPrize) => void) {
    if (this.isSpinning) return;

    if (onComplete) {
      this.onSpinComplete = onComplete;
    }

    this.isSpinning = true;

    if (this.spinButton) {
      this.spinButton.disableInteractive();
    }

    // Random number of full rotations + random final angle
    const fullRotations = Phaser.Math.Between(5, 8);
    const randomSegment = Phaser.Math.Between(0, this.prizes.length - 1);
    const segmentAngle = (Math.PI * 2) / this.prizes.length;
    const finalAngle =
      fullRotations * Math.PI * 2 + randomSegment * segmentAngle;

    // Animate the spin
    this.currentTween = this.scene.tweens.add({
      targets: this.wheel,
      rotation: finalAngle,
      duration: 4000,
      ease: "Cubic.easeOut",
      onComplete: () => {
        this.isSpinning = false;
        const winningPrize = this.prizes[randomSegment];

        if (this.onSpinComplete) {
          this.onSpinComplete(winningPrize);
        }

        if (this.spinButton) {
          this.spinButton.setInteractive({ useHandCursor: true });
        }
      },
    });
  }

  public setSpinButton(button: Phaser.GameObjects.Rectangle) {
    this.spinButton = button;
  }

  public destroy() {
    // Stop any ongoing tween
    if (this.currentTween) {
      this.currentTween.stop();
      this.currentTween = null;
    }
    this.segments.forEach((s) => s.destroy());
    this.prizeIcons.forEach((p) => p.destroy());
    this.prizeTexts.forEach((t) => t.destroy());
    if (this.centerPointer) {
      this.centerPointer.destroy();
    }
    if (this.pointerShadow) {
      this.pointerShadow.destroy();
    }
    if (this.wheelShadow) {
      this.wheelShadow.destroy();
    }
    this.wheel.destroy();
    this.container.destroy();
  }

  public getContainer(): Phaser.GameObjects.Container {
    return this.container;
  }
}
